import React, { useEffect, useMemo } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { IS_MOBILE } from '../config';
import { freeGltf } from '../utils/gltfMemory';

// CitySkyline — an "endless city" backdrop: copies of a low-poly night-skyline
// chunk arranged in a ring around the world, facing inward, far out past the
// playable area. Night-lit window textures make it glow at dusk. Combined with
// the walk boundary, you never reach the ring, so the city reads as infinite.

const MODEL_URL = '/models/skyline.glb';
const RING_RADIUS = 300;   // how far out the backdrop sits
const TARGET_HEIGHT = 95;  // scaled building height at that distance
const FACING = Math.PI;    // flip by ±Math.PI if the buildings face outward
const SINK_FRAC = 0.025;    // sink this fraction of the (scaled) height below ground so no base floats

export default function CitySkyline() {
  const { scene } = useGLTF(MODEL_URL);
  // Mobile: dump this 10MB backdrop's GPU memory on unmount (entering a splat
  // interior) so the splat fits; it reloads on return. Desktop keeps it cached.
  useEffect(() => {
    if (!IS_MOBILE) return undefined;
    return () => freeGltf(scene, MODEL_URL);
  }, [scene]);

  // The ring sits past the scene fog's far plane — turn fog off on its
  // materials so it stays visible as the backdrop instead of fading out.
  useMemo(() => {
    scene.traverse((o) => {
      if (!o.material) return;
      (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => { m.fog = false; });
    });
  }, [scene]);

  const items = useMemo(() => {
    const box = new THREE.Box3().setFromObject(scene);
    const size = new THREE.Vector3(); box.getSize(size);
    const s = TARGET_HEIGHT / (size.y || 1);
    const pieceW = Math.max(1, (size.x || 1) * s);
    // Sink the ring so every building base is below the ground plane (which
    // occludes them) — hides the model's uneven/floating bases.
    const groundY = -box.min.y * s - (size.y * s * SINK_FRAC);
    // Enough pieces to wrap the ring with slight overlap (no gaps).
    const n = Math.max(10, Math.ceil((2 * Math.PI * RING_RADIUS) / (pieceW * 0.85)));
    return Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2;
      const x = Math.cos(a) * RING_RADIUS;
      const z = Math.sin(a) * RING_RADIUS;
      return { obj: scene.clone(true), pos: [x, groundY, z], rotY: Math.atan2(x, z) + FACING, scale: s };
    });
  }, [scene]);

  return (
    <group>
      {/* Dark ground filling the gap between the model city and the ring, so
          there's no void underneath the distant skyline. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.3, 0]}>
        <circleGeometry args={[RING_RADIUS + 40, 64]} />
        <meshBasicMaterial color="#0c0c12" toneMapped={false} fog={false} />
      </mesh>
      {items.map((it, i) => (
        <primitive key={i} object={it.obj} position={it.pos} rotation={[0, it.rotY, 0]} scale={it.scale} />
      ))}
    </group>
  );
}

useGLTF.preload(MODEL_URL);
